import { useMemo, useState } from "react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ToolCard from "../components/ToolCard";
import SEO from "../components/SEO";

const tools = [
  {
    title: "EMI Calculator",
    path: "/emi-calculator",
    category: "Finance",
    description:
      "Calculate monthly loan EMI instantly with amortization schedule.",
  },

  {
    title: "Tax Calculator",
    path: "/tax-calculator",
    category: "Finance",
    description:
      "Compare old vs new tax regime instantly.",
  },

  {
    title: "Age Calculator",
    path: "/age-calculator",
    category: "Utility",
    description:
      "Calculate exact age with detailed results.",
  },

  {
    title: "Password Generator",
    path: "/password-generator",
    category: "Utility",
    description:
      "Generate strong secure random passwords.",
  },

  {
    title: "QR Generator",
    path: "/qr-generator",
    category: "Utility",
    description:
      "Create QR codes instantly online.",
  },

  {
    title: "Word Counter",
    path: "/word-counter",
    category: "Utility",
    description:
      "Count words, characters, and reading time.",
  },

  {
    title: "JSON Formatter",
    path: "/json-formatter",
    category: "Developer",
    description:
      "Format, validate, and beautify JSON data.",
  },

  {
    title: "Merge PDF",
    path: "/merge-pdf",
    category: "PDF",
    description:
      "Combine multiple PDF files into one document.",
  },

  {
    title: "Split PDF",
    path: "/split-pdf",
    category: "PDF",
    description:
      "Extract and split PDF pages online.",
  },

  {
    title: "Image to PDF",
    path: "/image-to-pdf",
    category: "PDF",
    description:
      "Convert JPG and PNG images into PDF documents.",
  },

  {
    title: "Stopwatch",
    path: "/stopwatch",
    category: "Utility",
    description:
      "Professional stopwatch with lap timer.",
  },

  {
    title: "BMI Calculator",
    path: "/bmi-calculator",
    category: "Health",
    description:
      "Calculate BMI and check body weight category.",
  },

  {
    title: "Percentage Calculator",
    path: "/percentage-calculator",
    category: "Finance",
    description:
      "Calculate percentages using multiple methods.",
  },

  {
    title: "Loan Eligibility",
    path: "/loan-eligibility",
    category: "Finance",
    description:
      "Check loan eligibility instantly online.",
  },

  {
    title: "JWT Decoder",
    path: "/jwt-decoder",
    category: "Developer",
    description:
      "Decode JWT tokens securely in browser.",
  },

];

export default function Home() {

  const [search,
    setSearch] =
    useState("");

  const filteredTools =
    useMemo(() => {

      return tools.filter((tool) =>
        tool.title
          .toLowerCase()
          .includes(
            search.toLowerCase()
          )
      );

    }, [search]);

  const categories =
    [
      "Finance",
      "Utility",
      "Developer",
      "PDF",
      "Health",
    ];

  return (
    <>
      <SEO
        title="Free Online Tools Center"
        description="Free online finance, utility, PDF, health, and developer tools. Fast, secure, and mobile-friendly tools for everyday use."
        keywords="online tools, pdf tools, finance calculators, utility tools, developer tools, free calculators"
        url="https://online-tools-center.vercel.app"
      />

      <Navbar />

      {/* HERO SECTION */}

      <section
        style={{
          background:
            "linear-gradient(135deg,#0f172a,#1e293b)",
          color: "white",
          padding:
            "80px 20px 70px",
        }}
      >

        <div className="container">

          <div className="row align-items-center">

            <div className="col-lg-7">

              <div
                style={{
                  maxWidth: "700px",
                }}
              >

                <span
                  className="badge bg-primary mb-3 p-2"
                >
                  100% Free Online Tools
                </span>

                <h1
                  className="fw-bold mb-4"
                  style={{
                    fontSize:
                      "clamp(2.5rem,5vw,4rem)",
                    lineHeight: "1.2",
                  }}
                >
                  Free Online Tools
                  for Daily Productivity
                </h1>

                <p
                  className="lead text-light mb-4"
                >
                  Use modern finance,
                  developer, PDF,
                  productivity, and utility
                  tools directly in your
                  browser without signup.
                </p>

                {/* SEARCH */}

                <div
                  className="bg-white rounded-4 shadow-lg p-2 d-flex flex-column flex-md-row gap-2"
                  style={{
                    maxWidth: "650px",
                  }}
                >

                  <input
                    type="text"
                    className="form-control border-0 shadow-none"
                    placeholder="Search tools..."
                    value={search}
                    onChange={(e) =>
                      setSearch(
                        e.target.value
                      )
                    }
                    style={{
                      minHeight: "55px",
                    }}
                  />

                  <button
                    className="btn btn-primary px-4"
                    style={{
                      minHeight: "55px",
                    }}
                  >
                    Search
                  </button>

                </div>

                {/* STATS */}

                <div className="d-flex flex-wrap gap-4 mt-5">

                  <div>

                    <h3 className="fw-bold">
                      15+
                    </h3>

                    <p className="mb-0 text-light">
                      Online Tools
                    </p>

                  </div>

                  <div>

                    <h3 className="fw-bold">
                      100%
                    </h3>

                    <p className="mb-0 text-light">
                      Free Access
                    </p>

                  </div>

                  <div>

                    <h3 className="fw-bold">
                      Secure
                    </h3>

                    <p className="mb-0 text-light">
                      Browser Based
                    </p>

                  </div>

                </div>

              </div>

            </div>

            {/* HERO RIGHT */}

            <div className="col-lg-5 mt-5 mt-lg-0">

              <div className="row g-3">

                {tools
                  .slice(0, 4)
                  .map((tool) => (

                    <div
                      className="col-6"
                      key={tool.path}
                    >

                      <div
                        className="card border-0 shadow-lg rounded-4 h-100"
                        style={{
                          minHeight: "150px",
                        }}
                      >

                        <div className="card-body">

                          <span className="badge bg-light text-dark mb-2">
                            {tool.category}
                          </span>

                          <h5 className="fw-bold">
                            {tool.title}
                          </h5>

                          <p
                            className="text-muted small"
                          >
                            {
                              tool.description
                            }
                          </p>

                        </div>

                      </div>

                    </div>

                  ))}

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* CATEGORY SECTION */}

      <section className="py-5 bg-light">

        <div className="container">

          <div className="text-center mb-5">

            <h2 className="fw-bold">
              Browse Categories
            </h2>

            <p className="text-muted">
              Explore tools by category
            </p>

          </div>

          <div className="row g-4">

            {categories.map(
              (category, index) => (

                <div
                  className="col-6 col-md-4 col-lg"
                  key={index}
                >

                  <div className="card border-0 shadow-sm rounded-4 text-center h-100">

                    <div className="card-body py-4">

                      <h5 className="fw-bold">
                        {category}
                      </h5>

                      <p className="text-muted mb-0">
                        {
                          tools.filter(
                            (tool) =>
                              tool.category ===
                              category
                          ).length
                        }{" "}
                        Tools
                      </p>

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </section>

      {/* TOOLS */}

      <section className="py-5">

        <div className="container">

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">

            <div>

              <h2 className="fw-bold">
                Popular Tools
              </h2>

              <p className="text-muted">
                Fast and secure browser-based tools
              </p>

            </div>

          </div>

          <div className="tools-grid">

            {filteredTools.map((tool) => (

              <ToolCard
                key={tool.path}
                title={tool.title}
                description={
                  tool.description
                }
                category={
                  tool.category
                }
                path={tool.path}
              />

            ))}

          </div>

        </div>

      </section>

      {/* WHY CHOOSE */}

      <section className="py-5 bg-light">

        <div className="container">

          <div className="row g-4">

            <div className="col-lg-6">

              <h2 className="fw-bold mb-4">
                Why Choose Our Tools?
              </h2>

              <p className="text-muted">
                Our platform provides
                fast, responsive, and
                privacy-friendly online
                tools for students,
                professionals, developers,
                and businesses.
              </p>

              <ul className="mt-4">

                <li className="mb-3">
                  100% free online tools
                </li>

                <li className="mb-3">
                  Mobile-friendly design
                </li>

                <li className="mb-3">
                  No signup required
                </li>

                <li className="mb-3">
                  Secure browser processing
                </li>

                <li className="mb-3">
                  Fast calculations
                </li>

                <li className="mb-3">
                  Modern responsive UI
                </li>

              </ul>

            </div>

            <div className="col-lg-6">

              <div className="row g-3">

                <div className="col-6">

                  <div className="card border-0 shadow rounded-4 text-center p-4 h-100">

                    <h3 className="fw-bold text-primary">
                      Fast
                    </h3>

                    <p className="mb-0">
                      Instant calculations
                    </p>

                  </div>

                </div>

                <div className="col-6">

                  <div className="card border-0 shadow rounded-4 text-center p-4 h-100">

                    <h3 className="fw-bold text-success">
                      Secure
                    </h3>

                    <p className="mb-0">
                      Browser-based tools
                    </p>

                  </div>

                </div>

                <div className="col-6">

                  <div className="card border-0 shadow rounded-4 text-center p-4 h-100">

                    <h3 className="fw-bold text-danger">
                      Free
                    </h3>

                    <p className="mb-0">
                      No hidden charges
                    </p>

                  </div>

                </div>

                <div className="col-6">

                  <div className="card border-0 shadow rounded-4 text-center p-4 h-100">

                    <h3 className="fw-bold text-warning">
                      Responsive
                    </h3>

                    <p className="mb-0">
                      Works on all devices
                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* FAQ */}

      <section className="py-5">

        <div className="container">

          <div className="text-center mb-5">

            <h2 className="fw-bold">
              Frequently Asked Questions
            </h2>

          </div>

          <div className="accordion" id="faqAccordion">

            <div className="accordion-item">

              <h2 className="accordion-header">

                <button
                  className="accordion-button"
                  data-bs-toggle="collapse"
                  data-bs-target="#faq1"
                >
                  Are these tools free?
                </button>

              </h2>

              <div
                id="faq1"
                className="accordion-collapse collapse show"
              >

                <div className="accordion-body">

                  Yes, all tools are
                  completely free to use.

                </div>

              </div>

            </div>

            <div className="accordion-item">

              <h2 className="accordion-header">

                <button
                  className="accordion-button collapsed"
                  data-bs-toggle="collapse"
                  data-bs-target="#faq2"
                >
                  Are my files secure?
                </button>

              </h2>

              <div
                id="faq2"
                className="accordion-collapse collapse"
              >

                <div className="accordion-body">

                  Yes, most tools process
                  files directly inside your
                  browser.

                </div>

              </div>

            </div>

            <div className="accordion-item">

              <h2 className="accordion-header">

                <button
                  className="accordion-button collapsed"
                  data-bs-toggle="collapse"
                  data-bs-target="#faq3"
                >
                  Do I need to register?
                </button>

              </h2>

              <div
                id="faq3"
                className="accordion-collapse collapse"
              >

                <div className="accordion-body">

                  No registration or signup
                  is required.

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>

      <Footer />

    </>
  );
}